import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleLuxuryVancouverExperiences() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "luxury-vancouver-experiences-worth-it";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "fine-dining": "Fine Dining",
    "private-yachts": "Private Yacht & Boat Tours",
    "helitours": "Helicopter & Seaplane Tours",
    "spas": "Luxury Spas & Wellness Retreats",
    "shopping": "Exclusive Shopping",
    "luxury-hotels": "Luxury Hotels & Stays",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Combined official resources + article references
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Site",
    },
    {
      url: "https://www.hellobc.com/places-to-go/vancouver/",
      label: "Hello BC – Vancouver Travel Guide",
    },
    {
      url: "https://www.bewhalewise.org/",
      label: "Be Whale Wise – Marine Wildlife Guidelines",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed, each "id" referencing the section
  // or specific mention in the article.
  const locations = [
    // Fine Dining
    {
      id: "hawksworth",
      lat: 49.28344,
      lng: -123.11891,
      popupText: "Hawksworth Restaurant",
    },
    {
      id: "st-lawrence",
      lat: 49.2833,
      lng: -123.098,
      popupText: "St. Lawrence Restaurant",
    },
    {
      id: "masayoshi",
      lat: 49.24583,
      lng: -123.09,
      popupText: "Masayoshi (Omakase Sushi)",
    },

    // Yachts
    {
      id: "van-lux-charters",
      lat: 49.27583,
      lng: -123.10972,
      popupText: "Vancouver Luxury Charters (Plaza of Nations Marina)",
    },

    // Heli & Seaplane
    {
      id: "harbour-air",
      lat: 49.29444,
      lng: -123.11139,
      popupText: "Harbour Air Seaplanes (Downtown Terminal)",
    },
    {
      id: "helijet",
      lat: 49.28699,
      lng: -123.10629,
      popupText: "Vancouver Harbour Heliport",
    },
    {
      id: "whistler-heli",
      lat: 50.11632,
      lng: -122.95736,
      popupText: "Whistler Heli-Skiing Office",
    },

    // Spas
    {
      id: "chi-spa",
      lat: 49.28583,
      lng: -123.12361,
      popupText: "CHI, The Spa at Shangri-La",
    },
    {
      id: "willow-stream-spa",
      lat: 49.28806,
      lng: -123.11639,
      popupText: "Willow Stream Spa at Fairmont Pacific Rim",
    },
    {
      id: "miraj-spa",
      lat: 49.26667,
      lng: -123.139,
      popupText: "Miraj Hammam Spa",
    },

    // Shopping
    {
      id: "alberni-luxury-zone",
      lat: 49.2845,
      lng: -123.1215,
      popupText: "Alberni Street 'Luxury Zone'",
    },
    {
      id: "holt-renfrew",
      lat: 49.2829,
      lng: -123.117,
      popupText: "Holt Renfrew Vancouver (Pacific Centre)",
    },

    // Hotels
    {
      id: "hotel-georgia",
      lat: 49.28344,
      lng: -123.11891,
      popupText: "Rosewood Hotel Georgia",
    },
    {
      id: "fairmont-pacific-rim",
      lat: 49.28806,
      lng: -123.11639,
      popupText: "Fairmont Pacific Rim",
    },
    {
      id: "shangri-la",
      lat: 49.28583,
      lng: -123.12361,
      popupText: "Shangri-La Vancouver",
    },
    {
      id: "wedgewood",
      lat: 49.2815,
      lng: -123.1209,
      popupText: "Wedgewood Hotel & Spa",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Luxury Vancouver Experiences: Are They Worth It?</title>
        <meta
          name="description"
          content="An in-depth guide to Vancouver's top luxury experiences: fine dining, private yachts, helicopter tours, spa retreats, exclusive shopping, and more."
        />
        <meta
          name="keywords"
          content="Vancouver luxury, fine dining, private tours, yacht, helicopter, spa, high-end shopping"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/luxury-vancouver-experiences-worth-it.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Luxury Vancouver Experiences: Are They Worth It?"
        />
        <meta
          property="og:description"
          content="An in-depth guide to Vancouver's finest luxury experiences, from fine dining and yacht charters to helicopter tours and world-class spas."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/luxury-vancouver-experiences-worth-it.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/luxury-vancouver-experiences-worth-it.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Luxury Vancouver Experiences: Are They Worth It?"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's top-tier luxury scene with our curated guide to the best high-end restaurants, private tours, exclusive shopping, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/luxury-vancouver-experiences-worth-it.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A luxurious view of Vancouver's skyline, waterfront, and mountains"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver is a dynamic city where modern elegance meets
              breathtaking nature, making it a haven for high-end travelers.
              From Michelin-recognized restaurants to private yacht cruises and
              helicopter tours, this coastal metropolis has developed a thriving
              luxury scene that caters to honeymooners, business travelers, and
              adventure seekers alike. In this article, we explore the most
              sought-after experiences, complete with practical tips, costs, and
              location details—so you can decide if Vancouver’s luxury offerings
              are truly “worth it.”
            </p>

            {/* SECTION: Fine Dining */}
            <h2
              id="fine-dining"
              ref={(el) => setSpotRef("fine-dining", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Fine Dining
            </h2>
            <p>
              Vancouver’s culinary scene is world-class, featuring a host of
              acclaimed chefs and fresh, locally sourced ingredients. Here are a
              few standout options for those looking to indulge in top-tier
              dining:
            </p>
            <ul>
              <li ref={(el) => setSpotRef("hawksworth", el)}>
                <strong>Hawksworth Restaurant</strong>{" "}
                <Marker
                  coordY={49.28344}
                  coordX={-123.11891}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Located in the historic Rosewood Hotel Georgia, Hawksworth
                offers seasonal tasting menus and an elegant ambiance. Prices
                typically range from CAD $99 to $125 per person for tasting
                menus (wine pairing extra).
              </li>
              <li ref={(el) => setSpotRef("st-lawrence", el)}>
                <strong>St. Lawrence Restaurant</strong>{" "}
                <Marker
                  coordY={49.2833}
                  coordX={-123.098}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – An intimate 30-seat bistro specializing in French-Canadian
                cuisine. The set multi-course menu runs about CAD $148 per
                person, with rich flavors inspired by Quebecois classics.
              </li>
              <li ref={(el) => setSpotRef("masayoshi", el)}>
                <strong>Masayoshi</strong>{" "}
                <Marker
                  coordY={49.24583}
                  coordX={-123.09}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – A premium omakase sushi bar with limited seating. Diners can
                expect to pay around CAD $200 or more for a multi-course
                chef’s-choice experience.
              </li>
            </ul>
            <p>
              Reservations at these venues are almost always required—often
              weeks in advance. Dress codes lean toward “smart casual,” so feel
              free to dress up for an elevated night out.
            </p>

            {/* SECTION: Private Yachts */}
            <h2
              id="private-yachts"
              ref={(el) => setSpotRef("private-yachts", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Private Yacht &amp; Boat Tours
            </h2>
            <p>
              One of the most spectacular ways to experience Vancouver’s coastal
              setting is by booking a private yacht charter:
            </p>
            <p ref={(el) => setSpotRef("van-lux-charters", el)}>
              <strong>Vancouver Luxury Charters</strong>{" "}
              <Marker
                coordY={49.27583}
                coordX={-123.10972}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              offers a modern 50-foot yacht with customizable itineraries around
              English Bay, Howe Sound, and beyond. Expect to pay roughly
              CAD $449–499 per hour for up to 12 guests. This includes a skipper
              and can be upgraded with gourmet catering and photography. Popular
              routes include cruising under the Lions Gate Bridge, visiting
              Bowen Island, or anchoring for a sunset champagne toast.
            </p>

            {/* SECTION: Helicopter & Seaplane Tours */}
            <h2
              id="helitours"
              ref={(el) => setSpotRef("helitours", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Helicopter &amp; Seaplane Tours
            </h2>
            <p>
              To get a true bird’s-eye view of Vancouver and its breathtaking
              surroundings, consider these aerial adventures:
            </p>
            <ul>
              <li ref={(el) => setSpotRef("harbour-air", el)}>
                <strong>Harbour Air Seaplanes</strong>{" "}
                <Marker
                  coordY={49.29444}
                  coordX={-123.11139}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Offers scenic flights from Coal Harbour starting around
                CAD $170–250 per person for a 20–45 minute aerial tour of the
                city, Stanley Park, and the surrounding mountains.
              </li>
              <li ref={(el) => setSpotRef("helijet", el)}>
                <strong>Helijet / SKY Helicopters</strong>{" "}
                <Marker
                  coordY={49.28699}
                  coordX={-123.10629}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Helicopter tours begin at around CAD $285–300 per person for a
                short city scenic flight. Private tours often start around
                CAD $1,600 for a group of up to four. Popular add-ons include
                mountaintop picnics, backcountry landings, or heli-skiing in
                winter.
              </li>
              <li ref={(el) => setSpotRef("whistler-heli", el)}>
                <strong>Whistler Heli-Skiing</strong>{" "}
                <Marker
                  coordY={50.11632}
                  coordX={-122.95736}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – For advanced skiers and snowboarders, heli-skiing packages
                start around CAD $1,600 per person for three runs in pristine
                backcountry powder.
              </li>
            </ul>

            {/* SECTION: Luxury Spas & Wellness */}
            <h2
              id="spas"
              ref={(el) => setSpotRef("spas", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Luxury Spas &amp; Wellness Retreats
            </h2>
            <p>
              After days of exploration, unwind at one of Vancouver’s renowned
              spa locations:
            </p>
            <ul>
              <li ref={(el) => setSpotRef("chi-spa", el)}>
                <strong>CHI, The Spa</strong>{" "}
                <Marker
                  coordY={49.28583}
                  coordX={-123.12361}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                at Shangri-La features holistic treatments drawn from Asian
                healing traditions. Expect to pay around CAD $200 for a single
                treatment.
              </li>
              <li ref={(el) => setSpotRef("willow-stream-spa", el)}>
                <strong>Willow Stream Spa</strong>{" "}
                <Marker
                  coordY={49.28806}
                  coordX={-123.11639}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                at the Fairmont Pacific Rim offers panoramic harbor views, plus
                an outdoor terrace and hot tubs. Massages start at about CAD $150
                and up.
              </li>
              <li ref={(el) => setSpotRef("miraj-spa", el)}>
                <strong>Miraj Hammam Spa</strong>{" "}
                <Marker
                  coordY={49.26667}
                  coordX={-123.139}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – A Middle Eastern–inspired hammam with steam treatments and
                massages, starting at around CAD $115 for a classic hammam
                experience.
              </li>
            </ul>

            {/* SECTION: Exclusive Shopping */}
            <h2
              id="shopping"
              ref={(el) => setSpotRef("shopping", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Exclusive Shopping
            </h2>
            <p>
              Luxury shoppers will find a wide array of high-end brands along
              Alberni Street and surrounding areas:
            </p>
            <p ref={(el) => setSpotRef("alberni-luxury-zone", el)}>
              The <strong>“Luxury Zone” on Alberni Street</strong>{" "}
              <Marker
                coordY={49.2845}
                coordX={-123.1215}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              showcases flagship stores like Gucci, Chanel, Tiffany &amp; Co.,
              and more. Most boutiques operate 10 am–7 pm on weekdays and
              slightly reduced hours on Sundays.
            </p>
            <p ref={(el) => setSpotRef("holt-renfrew", el)}>
              <strong>Holt Renfrew</strong>{" "}
              <Marker
                coordY={49.2829}
                coordX={-123.117}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              in Pacific Centre is a Canadian luxury department store with a
              personal shopping service and in-store café. It’s ideal for
              travelers seeking a one-stop shop for premium brands.
            </p>

            {/* SECTION: Luxury Hotels & Stays */}
            <h2
              id="luxury-hotels"
              ref={(el) => setSpotRef("luxury-hotels", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Luxury Hotels &amp; Stays
            </h2>
            <p>
              Vancouver’s 5-star hotels offer impeccable service, stunning
              suites, and world-class amenities:
            </p>
            <ul>
              <li ref={(el) => setSpotRef("hotel-georgia", el)}>
                <strong>Rosewood Hotel Georgia</strong>{" "}
                <Marker
                  coordY={49.28344}
                  coordX={-123.11891}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – A beautifully restored historic hotel in the heart of
                downtown. Rates often start around CAD $400 per night.
              </li>
              <li ref={(el) => setSpotRef("fairmont-pacific-rim", el)}>
                <strong>Fairmont Pacific Rim</strong>{" "}
                <Marker
                  coordY={49.28806}
                  coordX={-123.11639}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Overlooks Vancouver’s harbor and features a rooftop pool, an
                acclaimed spa, and modern décor. Nightly rates begin at about
                CAD $500.
              </li>
              <li ref={(el) => setSpotRef("shangri-la", el)}>
                <strong>Shangri-La Vancouver</strong>{" "}
                <Marker
                  coordY={49.28583}
                  coordX={-123.12361}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Occupies the first 15 floors of the city’s tallest building,
                providing sweeping skyline views. Rooms from around CAD $350 per
                night.
              </li>
              <li ref={(el) => setSpotRef("wedgewood", el)}>
                <strong>Wedgewood Hotel &amp; Spa</strong>{" "}
                <Marker
                  coordY={49.2815}
                  coordX={-123.1209}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – A boutique Relais &amp; Châteaux property noted for its
                old-world-style decor and personalized service.
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2
              id="local-regulations"
              ref={(el) => setSpotRef("local-regulations", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Local Regulations &amp; Best Practices
            </h2>
            <p>
              Vancouver’s luxury experiences adhere to strict safety and
              environmental guidelines. For marine life encounters, boats must
              remain at least 200 meters from whales; helicopter and seaplane
              companies prioritize safe flight paths and may cancel in poor
              weather. Drones are heavily restricted downtown and around
              Vancouver’s coastline, requiring special permits. Always review
              cancellation policies for hotels and tours, and book well in
              advance for peak season (June–August).
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. When is the best time to visit for luxury experiences?</h3>
              <p>
                Summer (June–August) sees the highest demand and best weather
                for yacht charters and scenic flights. Spring and fall are less
                crowded but still pleasant. Winter opens up unique offerings
                like heli-skiing near Whistler.
              </p>

              <h3>2. Do I need to tip for private tours or restaurant visits?</h3>
              <p>
                Yes, tipping is customary in Canada. Aim for 15–20% at upscale
                restaurants and around 10–20% for private guides and yacht
                charters. This can vary based on service quality.
              </p>

              <h3>3. Are these experiences suitable for families?</h3>
              <p>
                Many luxury options, such as private boats and seaplane tours,
                can be tailored for children. Fine dining restaurants and spas
                often have age restrictions or may not be kid-friendly. Always
                inquire in advance.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2
              id="conclusion"
              ref={(el) => setSpotRef("conclusion", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Conclusion
            </h2>
            <p>
              From Michelin-level dining to private yacht cruises and
              mountaintop helicopter picnics, Vancouver’s luxury experiences
              offer an incredible blend of urban sophistication and natural
              grandeur. While these indulgences can be pricey, the city’s
              top-tier service, breathtaking scenery, and remarkable culinary
              scene often justify the cost for those seeking a truly memorable
              getaway. Whether you’re celebrating a special occasion or simply
              looking to treat yourself, Vancouver’s luxury scene can deliver an
              unforgettable experience—if you plan ahead and savor every moment.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}